class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    document.addEventListener('click', FacetFiltersForm.onCollectionQuickFilterClick);
    document.addEventListener('change', FacetFiltersForm.onCollectionFilterChange, true);
    document.addEventListener('change', FacetFiltersForm.onMobileBarSortChange, true);
  }

  /*
   * Mobile sort must update only sort_by.
   * The drawer select lives in #FacetFiltersFormMobile, whose price
   * inputs are prefilled with the full range, so a normal form submit
   * also sends filter.v.price.* and the collection does not re-sort.
   * The bottom bar select is outside that form entirely.
   */
  static onMobileBarSortChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.name !== 'sort_by') return;

    const inBar = !!select.closest('.mobile-facets__sort_bottom');
    const inDrawer = !!select.closest('#FacetFiltersFormMobile');
    if (!inBar && !inDrawer) return;

    document
      .querySelectorAll(
        '#FacetFiltersFormMobile select[name="sort_by"], .mobile-facets__sort_bottom select[name="sort_by"]',
      )
      .forEach((other) => {
        if (other !== select) other.value = select.value;
      });

    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', select.value);
    FacetFiltersForm.renderPage(params.toString(), event);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    FacetFiltersForm.renderCollectionQuickFilter(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceGrid = parsedHtml.getElementById('ProductGridContainer');
    const targetGrid = document.getElementById('ProductGridContainer');

    if (!sourceGrid || !targetGrid) return;

    targetGrid.innerHTML = sourceGrid.innerHTML;

    targetGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    if (typeof window.initCollectionAjaxinate === 'function') {
      window.initCollectionAjaxinate();
    }

    if (typeof window.wishlistButtonsInit === 'function') {
      window.wishlistButtonsInit();
    } else if (typeof window.wishlistInit === 'function') {
      window.wishlistInit();
    }
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount =
      parsedHtml.getElementById('ProductCount') || parsedHtml.getElementById('ProductCountDesktop');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (!sourceCount) {
      updateEvent?.resolve(0);
      return;
    }

    const count = sourceCount.innerHTML;
    const productCount = sourceCount.dataset.productCount || '';
    const totalCount = sourceCount.dataset.totalCount || '';

    if (container) {
      container.innerHTML = count;
      container.dataset.productCount = productCount;
      container.dataset.totalCount = totalCount;
      container.classList.remove('loading');
    }

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      if (productCount) containerDesktop.dataset.productCount = productCount;
      if (totalCount) containerDesktop.dataset.totalCount = totalCount;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(productCount, 10) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderClearAllFilters(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }

    if (typeof initCustomPriceFacets === 'function') {
      initCustomPriceFacets();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelectorAll(selector).forEach((el) => {
        el.innerHTML = activeFacetsElement.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderClearAllFilters(html) {
    const parsedHtml = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;

    document.querySelectorAll('.js-clear-all-filters').forEach((target) => {
      const isMobileClear = target.classList.contains('mobile-facets__clear-wrapper');
      const source = isMobileClear
        ? parsedHtml.querySelector('.js-clear-all-filters.mobile-facets__clear-wrapper')
        : parsedHtml.querySelector('#FacetsWrapperDesktop > .js-clear-all-filters');

      if (!source) {
        if (!isMobileClear) target.classList.add('hidden');
        return;
      }

      const clone = source.cloneNode(true);
      if (isMobileClear) clone.classList.remove('hidden');
      target.replaceWith(clone);
    });
  }

  static renderCollectionQuickFilter(html) {
    const parsedHtml = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
    const source = parsedHtml.querySelector('#CollectionQuickFilter');
    const target = document.querySelector('#CollectionQuickFilter');

    if (source && target) {
      target.replaceWith(source.cloneNode(true));
      return;
    }

    if (!source && target) {
      target.remove();
      return;
    }

    if (source && !target) {
      const sectionPadding = document.querySelector('collection-component [class*="-padding"]');
      if (sectionPadding) {
        const breadcrumb = sectionPadding.querySelector('.page-width');
        if (breadcrumb) {
          breadcrumb.before(source.cloneNode(true));
        } else {
          sectionPadding.prepend(source.cloneNode(true));
        }
      }
    }
  }

  static getCollectionFilterParam() {
    return document.querySelector('#CollectionQuickFilter')?.dataset.filterParam || '';
  }

  static getSelectedCollectionValues(paramName) {
    const form =
      document.getElementById('FacetFiltersForm') || document.getElementById('FacetFiltersFormMobile');
    if (!form) return [];

    return Array.from(
      form.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(paramName)}"]:checked`),
    ).map((input) => input.value);
  }

  static syncCollectionQuickFilterUI(selectedValues) {
    const quickFilter = document.querySelector('#CollectionQuickFilter');
    if (!quickFilter) return;

    const values = Array.isArray(selectedValues) ? selectedValues : [];
    const hasActive = values.length > 0;

    quickFilter.querySelectorAll('.collections__filter-btn').forEach((button) => {
      const isAll = button.hasAttribute('data-collection-filter-all');
      const isActive = isAll ? !hasActive : values.includes(button.dataset.collectionFilterValue);

      button.classList.toggle('active', isActive);
      button.classList.toggle('button--primary', isActive);
      button.classList.toggle('button--secondary', !isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (button.hasAttribute('aria-checked')) {
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      }
    });
  }

  static setCollectionFilterValues(paramName, selectedValues) {
    const values = Array.isArray(selectedValues) ? selectedValues : [];

    document
      .querySelectorAll(`input[type="checkbox"][name="${CSS.escape(paramName)}"]`)
      .forEach((input) => {
        input.checked = values.includes(input.value);
      });

    FacetFiltersForm.syncCollectionQuickFilterUI(values);
  }

  static onCollectionFilterChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;

    const paramName = FacetFiltersForm.getCollectionFilterParam();
    if (!paramName || input.name !== paramName) return;

    // Keep desktop + mobile forms in sync for multi-select Collection filter
    document
      .querySelectorAll(`input[type="checkbox"][name="${CSS.escape(paramName)}"][value="${CSS.escape(input.value)}"]`)
      .forEach((checkbox) => {
        checkbox.checked = input.checked;
      });

    FacetFiltersForm.syncCollectionQuickFilterUI(FacetFiltersForm.getSelectedCollectionValues(paramName));
  }

  static onCollectionQuickFilterClick(event) {
    const allButton = event.target.closest('[data-collection-filter-all]');
    const valueButton = event.target.closest('[data-collection-filter-value]');
    if (!allButton && !valueButton) return;

    const quickFilter = event.target.closest('#CollectionQuickFilter');
    if (!quickFilter) return;

    event.preventDefault();

    const paramName = quickFilter.dataset.filterParam;
    if (!paramName) return;

    const form = document.getElementById('FacetFiltersForm') || document.getElementById('FacetFiltersFormMobile');
    if (!form) return;

    let selectedValues = [];

    if (allButton) {
      selectedValues = [];
    } else {
      const clickedValue = valueButton.dataset.collectionFilterValue;
      selectedValues = FacetFiltersForm.getSelectedCollectionValues(paramName);

      if (selectedValues.includes(clickedValue)) {
        selectedValues = selectedValues.filter((value) => value !== clickedValue);
      } else {
        selectedValues = [...selectedValues, clickedValue];
      }
    }

    FacetFiltersForm.setCollectionFilterValues(paramName, selectedValues);
    form.dispatchEvent(new Event('input', { bubbles: true }));
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid?.dataset?.id) return [];

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      const isEmptyPrice =
        (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') && String(value).trim() === '';
      if (isEmptyPrice) continue;
      params.append(key, value);
    }

    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (
      event.target instanceof HTMLSelectElement &&
      event.target.name === 'sort_by' &&
      event.target.closest('#FacetFiltersFormMobile')
    ) {
      return;
    }
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    // Clear-all returns to the collection URL with no filter params
    if (!url) {
      const paramName = FacetFiltersForm.getCollectionFilterParam();
      if (paramName) FacetFiltersForm.setCollectionFilterValues(paramName, []);
      FacetFiltersForm.filterData = [];
    }

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.closeFilter(event);
      }
    });
  }

  closeFilter(event) {
    event.preventDefault();
    event.stopPropagation();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (!form || typeof form.onActiveFilterClick !== 'function') return;
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
